import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    createFeeInvoice,
    CreateFeeInvoicePayload,
} from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import dayjs from 'dayjs'

export interface CreateFeeInvoiceModalProps extends ModalProps {
    serviceID: number
    service: Service
}

type FormValues = Omit<CreateFeeInvoicePayload, 'serviceID'>

const CreateFeeInvoiceModal: React.FC<CreateFeeInvoiceModalProps> = ({
    open,
    onClose,
    serviceID,
    service,
}) => {
    const { t } = useTranslation()

    // POC fees default to the trial period; fall back to today / +1 year
    const defaultStartDate =
        service?.is_poc && service?.service_start_date
            ? dayjs(service.service_start_date).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD')
    const defaultEndDate =
        service?.is_poc && service?.service_stopped_date
            ? dayjs(service.service_stopped_date).format('YYYY-MM-DD')
            : dayjs().add(1, 'year').format('YYYY-MM-DD')

    const { control, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            start_date: defaultStartDate,
            end_date: defaultEndDate,
            fee_amount: 0,
            fee_name: '',
            description: '',
        },
    })

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [
            `services.${serviceID}`,
            `services.updates.${serviceID}`,
            `services.invoices.${serviceID}`,
        ],
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createFeeInvoice,
        },
        {
            successMessage: t('create-service-invoice-fee-success'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_INVOICE,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const onSubmitHandler = async (data: FormValues) => {
        if (!data.fee_amount || data.fee_amount <= 0) return

        mutateAsync({
            serviceID,
            ...data,
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('service-create-invoice-fee')}
        >
            <form
                onSubmit={handleSubmit(onSubmitHandler)}
                className="space-y-4 mt-2"
            >
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-gray-600">
                        <span className="font-medium">{t('service')}:</span>{' '}
                        {service?.service_name}
                    </p>
                    <p className="text-sm text-gray-600">
                        <span className="font-medium">{t('customer')}:</span>{' '}
                        {service?.customer_name}
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <FormItem
                        control={control}
                        name="start_date"
                        type="datepicker"
                        label={`${t('start-date')} *`}
                    />

                    <FormItem
                        control={control}
                        name="end_date"
                        type="datepicker"
                        label={`${t('end-date')} *`}
                    />
                </div>

                <FormItem
                    control={control}
                    name="fee_name"
                    type="textfield"
                    label={t('fee-name')}
                    placeholder={t('fee-name-placeholder')}
                />

                <FormItem
                    control={control}
                    name="fee_amount"
                    type="number"
                    label={`${t('fee-amount')} *`}
                    placeholder={t('fee-amount-placeholder')}
                />

                <FormItem
                    control={control}
                    name="description"
                    type="textarea"
                    label={t('description')}
                    placeholder={t('description-placeholder')}
                />

                <div className="flex items-center justify-end gap-2">
                    <Button variant="outlined" onClick={onClose} type="button">
                        {t('cancel')}
                    </Button>
                    <Button
                        loading={isPending}
                        disabled={isPending}
                        type="submit"
                    >
                        {t('create-invoice')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateFeeInvoiceModal
